import React, { ErrorInfo } from 'react';
import * as Sentry from '@sentry/react';
import { Button, Result } from 'antd';
import { FormattedMessage, history } from 'umi';
import styles from './index.less';

type IProps = {
  children: React.ReactNode;
};

type IState = {
  hasError: boolean;
  error: Error | null;
};

class ErrorBoundary extends React.Component<IProps, IState> {
  unlisten: any = null;

  constructor(props: IProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  componentDidMount() {
    this.unlisten = history.listen(() => {
      if (this.state.hasError) {
        this.setState({ hasError: false, error: null });
      }
    });
  }

  componentWillUnmount() {
    if (this.unlisten) {
      this.unlisten();
    }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, { extra: { ...errorInfo } });
  }

  render() {
    if (this.state.hasError) {
      // @ts-ignore
      const isDev = typeof BACKOFFICE_ENV !== 'undefined' ? BACKOFFICE_ENV === 'dev' : process.env.NODE_ENV === 'development';

      if (isDev) {
        return (
          <div className={styles.devContainer}>
            <h1>Something went wrong (Development Mode)</h1>
            <pre className={styles.devPre}>{this.state.error?.stack}</pre>
            <Button onClick={() => window.location.reload()}>Reload Page</Button>
          </div>
        );
      }

      return (
        <div className={styles.errorContainer}>
          <Result
            status="500"
            title={
              <FormattedMessage
                id="error.boundary.title"
                defaultMessage="Something went wrong"
              />
            }
            subTitle={
              <FormattedMessage
                id="error.boundary.subtitle"
                defaultMessage="We caught the error already and are working on it."
              />
            }
            extra={
              <Button
                color={'primary'}
                variant={'filled'}
                onClick={() => window.location.reload()}
              >
                <FormattedMessage id={'common.reload'} defaultMessage={'Reload'} />
              </Button>
            }
          />
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
